// App.js - Fixed StatusBar overlap issue
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useOutletContext, useLocation } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import InactivityWarning from './components/Auth/InactivityWarning';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import StatusBar from './components/StatusBar';
import Transactions from './components/Transactions/Transactions';
import Groups from './components/Groups/Groups';
import PerformancePeriods from './components/Groups/PerformancePeriods';
import Users from './components/Users/Users';
import MemberAllocations from './components/Users/MemberAllocations';
import Loans from './components/Loans/Loans';
import LoansTableView from './components/Loans/LoansTableView';
import AdvanceLoans from './components/Loans/AdvanceLoans';
import Requests from './components/Requests/Requests';
import DiaryManagement from './components/Diary/Diary';
import CalendarView from './components/Diary/CalendarView';
import GroupView from './components/Diary/GroupsView';
import ExpenseTracker from './components/Diary/ExpenseTracker';
import HistoryView from './components/Diary/HistoryView';
import WorkingDaysConfig from './components/Diary/WorkingDaysConfig';
import MetricsDashboard from './components/Metrics/Metrics';
import Receipts from './components/Receipts/Receipts';
import PerformanceAnalysis from './components/Groups/performanceAnalysis/PerformanceAnalysis';
import AdminPanel from './components/Auth/Admin';
import Login from './components/Auth/Login';
import Fieldwork from './components/Fieldwork/Fieldwork';
import ConsolidatedWithdrawals from './components/Fieldwork/ConsolidatedWithdrawals';
import ExecuteRequest from './components/Fieldwork/ExecuteRequest';
import ReportsDashboard from './components/Financials/ReportsDashboard';
import UpdateNotifier from './components/UpdateNotifier';
import { getTheme } from './theme';
import { SchoolFeesProvider } from './components/Groups/SchoolFeesContext';

// Create a Theme Context
const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider');
  }
  return context;
};

// Helper functions for theme persistence using localStorage (works in Electron)
const THEME_STORAGE_KEY = 'app_theme_mode';

const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'dark';
  } catch (error) {
    console.error('Error reading theme from localStorage:', error);
    return false; // Default to light mode
  }
};

const setStoredTheme = (isDark) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
  } catch (error) {
    console.error('Error saving theme to localStorage:', error);
  }
};

// StatusBar height constant - used across the app
export const STATUS_BAR_HEIGHT = 32;

// Custom scrollbar styles
const scrollbarStyles = {
  '*::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '*::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '4px',
  },
  '*::-webkit-scrollbar-thumb': {
    background: 'rgba(128, 128, 128, 0.5)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(128, 128, 128, 0.7)',
    },
  },
  // Firefox scrollbar
  '*': {
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(128, 128, 128, 0.5) rgba(0, 0, 0, 0.1)',
  },
};

// Diary's Calendar page needs the `currentDate` state that lives in the
// shared Diary layout (components/Diary/Diary.js), since the month
// navigation controls live in that layout's header, not on this page.
// Diary.js passes it down via <Outlet context={{ currentDate }} />.
const DiaryCalendarPage = () => {
  const { currentDate } = useOutletContext();
  return <CalendarView currentDate={currentDate} />;
};

// Main App Content Component (uses auth context)
const AppContent = () => {
  const { isDark, toggleTheme } = useThemeMode();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { isAuthenticated, isLoading } = useAuth();
  const isElectron = window.electronAPI !== undefined;
  const location = useLocation();
  const mainContentRef = useRef(null);

  const toggleSidebar = () => setIsCollapsed(!isCollapsed);

  // <Box component="main"> below is mounted once and never remounted
  // between routes — only the <Routes> children inside it swap — so its
  // scroll position otherwise carries over from whatever page you were
  // just on. Most navigation never scrolls far enough for that to be
  // visible, but any page that does scroll it (e.g. Performance Periods
  // scrolling to a highlighted row) leaves every subsequent page stuck at
  // that same scroll position. Reset it on every route change instead.
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
    // Belt-and-suspenders: also reset the window/document itself. main's
    // own scrollTop is the one actually meant to move in this layout, but
    // if anything (a scrollIntoView call, a browser quirk) ever scrolls
    // the window instead, nothing else resets that on a client-side route
    // change — it would otherwise sit wherever it landed, on every page,
    // forever.
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [location.pathname]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/management/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/management/login" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <Navbar toggleTheme={toggleTheme} isDark={isDark} isCollapsed={isCollapsed} />
        <Sidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
        <Box
          component="main"
          ref={mainContentRef}
          sx={{
            flexGrow: 1,
            height: '100vh',
            marginTop: '80px',
            padding: 3,
            // Increase bottom padding to account for StatusBar + comfortable spacing
            paddingBottom: isElectron ? `${STATUS_BAR_HEIGHT + 24}px` : '24px',
            overflow: 'auto',
            boxSizing: 'border-box',
            // Add scroll padding to ensure content doesn't hide behind StatusBar
            scrollPaddingBottom: isElectron ? `${STATUS_BAR_HEIGHT + 16}px` : '16px',
            // Chrome's scroll anchoring tries to "compensate" for layout
            // shifts above the current scroll position (e.g. a page's
            // loading spinner being swapped for its real content) by
            // silently moving scrollTop — which was undoing the
            // reset-on-navigate effect above, after the fact. Turn it off
            // so scrollTop only ever changes when we explicitly set it.
            overflowAnchor: 'none',
          }}
        >
          <Routes>
            <Route path="/management/transactions" element={<Transactions />} />
            {/* Was the "Bank Transactions" tab inside Fieldwork.js
                (ConsolidatedWithdrawals) — now its own page. */}
            <Route path="/management/payments/outgoing" element={<ConsolidatedWithdrawals />} />
            <Route path="/management" element={<Navigate to="/management/transactions" replace />} />
            <Route path="/management/groups" element={<Groups />} />
            <Route path="/management/groups/performance-periods" element={<PerformancePeriods isDark={isDark} />} />
            <Route path="/management/users" element={<Users />} />
            <Route path="/management/users/allocations" element={<MemberAllocations isDark={isDark} />} />
            <Route path="/management/loans" element={<Loans isDark={isDark} />} />
            <Route path="/management/loans/long-term" element={<LoansTableView isDark={isDark} />} />
            <Route path="/management/loans/advance" element={<AdvanceLoans isDark={isDark} />} />
            <Route path="/management/requests" element={<Requests />} />
            {/* Was the "Execute Requests" tab inside Fieldwork.js — now its
                own page, below Member Requests. */}
            <Route path="/management/users/execute-requests" element={<ExecuteRequest />} />
            {/* Diary is now a section: the sidebar links straight to each
                sub-page instead of Diary.js switching between internal tabs. */}
            <Route path="/management/diary" element={<DiaryManagement />}>
              <Route index element={<Navigate to="calendar" replace />} />
              <Route path="calendar" element={<DiaryCalendarPage />} />
              <Route path="groups" element={<GroupView />} />
              <Route path="expenses" element={<ExpenseTracker />} />
              <Route path="history" element={<HistoryView />} />
              <Route path="working-days" element={<Box sx={{ p: 3 }}><WorkingDaysConfig /></Box>} />
            </Route>
            <Route path="/management/metrics" element={<MetricsDashboard isDark={isDark} />} />
            {/* Financials: revenue, loan and advance reports with Excel downloads */}
            <Route path="/management/financials/reports" element={<ReportsDashboard isDark={isDark} />} />
            <Route path="/management/receipts" element={<Receipts />} />
            <Route path="/management/admin" element={<AdminPanel />} />
            <Route path="/management/fieldwork" element={<Fieldwork />} />
            <Route path="/management/group-performance/:groupId" element={<PerformanceAnalysis />} />
            <Route path="*" element={<Navigate to="/management" replace />} />
          </Routes>
        </Box>
      </Box>
      {/* StatusBar renders outside the main flex container to span full width */}
      {isElectron && <StatusBar />}
    </>
  );
};

// Theme Provider Wrapper - Single source of truth for theme state with persistence
// Note: Zoom level is also persisted automatically by Electron (saved in userData/zoom-config.json)
const ThemeProviderWrapper = ({ children }) => {
  // Initialize theme from localStorage
  const [isDark, setIsDark] = useState(() => getStoredTheme());
  const [isInitialized, setIsInitialized] = useState(false);

  const theme = getTheme(isDark);

  const toggleTheme = () => {
    setIsDark((prev) => {
      const newTheme = !prev;
      setStoredTheme(newTheme);
      return newTheme;
    });
  };

  // Initialize theme on mount
  useEffect(() => {
    const savedTheme = getStoredTheme();
    setIsDark(savedTheme);
    setIsInitialized(true);

    // Optional: Sync with system preference on first load if no saved preference
    const storedValue = localStorage.getItem(THEME_STORAGE_KEY);
    if (!storedValue) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        setIsDark(true);
        setStoredTheme(true);
      }
    }
  }, []);

  // Prevent flash of wrong theme
  if (!isInitialized) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={scrollbarStyles} />
      <ThemeContext.Provider value={{ isDark, toggleTheme }}>
        {children}
      </ThemeContext.Provider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <SchoolFeesProvider>
        <ThemeProviderWrapper>
          <AppContent />
          <InactivityWarning />
          <UpdateNotifier />
        </ThemeProviderWrapper>
        </SchoolFeesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;